import React, { useState } from 'react';
import { Table, Badge, Space, Button } from "antd";
import { Link, useParams } from 'react-router-dom';
import Header from '../Header';
import Sidebar from '../Sidebar';
import FeatherIcon from "feather-icons-react";
import { usePatientVisits } from '../../hooks/useVisits';
import { usePatient } from '../../hooks/usePatients';
import { getVisitsByPatientId } from '../../data/mockVisits';
import { onShowSizeChange, itemRender } from '../Pagination';
import { plusicon, refreshicon, searchnormal, imagesend } from '../imagepath';

const PatientVisitsHistory = () => {
    const { patientId } = useParams();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch patient data and visits
    const { data: patient, isLoading: patientLoading } = usePatient(patientId);
    // const { data: visits = [], isLoading: visitsLoading, refetch } = usePatientVisits(patientId);
    
    // Use mock data for testing
    const visits = getVisitsByPatientId(patientId);
    const visitsLoading = false;

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    // Filter visits based on search term
    const filteredVisits = visits.filter(visit =>
        visit.noAl.toLowerCase().includes(searchTerm.toLowerCase()) ||
        visit.mainDiagnosis.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        visit.mainDiagnosis.mkbCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        visit.doctor.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getVisitTypeLabel = (visitType) => {
        if (visitType.consultation) return "Консултация";
        if (visitType.dispensary) return "Диспансер";
        if (visitType.babyCare) return "Грижи за бебе";
        if (visitType.prophylactic?.over18) return "Профилактика 18+";
        if (visitType.prophylactic?.child) return "Детска профилактика";
        return "Стандартен";
    };

    const getExamTypeLabel = (examType) => {
        switch (examType) {
            case 11: return "Първичен";
            case 12: return "Контролен";
            case 13: return "Профилактичен";
            default: return `Тип ${examType}`;
        }
    };

    const columns = [
        {
            title: "№ Амбулаторен лист",
            dataIndex: "noAl",
            width: 150,
            className: "text-nowrap",
            render: (text, record) => (
                <Link to={`/patients/${patientId}/visits/${record.id}`} className="text-primary">
                    {text}
                </Link>
            ),
        },
        {
            title: "Дата и час",
            dataIndex: "visitDate",
            width: 120,
            className: "text-nowrap",
            render: (text, record) => (
                <div>
                    <div>{new Date(record.visitDate).toLocaleDateString('bg-BG')}</div>
                    <small className="text-muted">{record.visitTime}</small>
                </div>
            ),
        },
        {
            title: "Лекар",
            dataIndex: ["doctor", "fullName"],
            width: 200,
            className: "text-nowrap",
            render: (text, record) => (
                <div>
                    <div className="text-sm">{record.doctor.fullName}</div>
                    <small className="text-muted">
                        УИН: {record.doctor.uinNumber}
                        {record.doctor.isSubstitute && " (Заместник)"}
                    </small>
                </div>
            ),
        },
        {
            title: "Основна диагноза",
            dataIndex: ["mainDiagnosis", "name"],
            width: 250,
            className: "text-nowrap",
            render: (text, record) => (
                <div>
                    <div className="text-sm">{record.mainDiagnosis.name}</div>
                    <Badge color="blue">{record.mainDiagnosis.mkbCode}</Badge>
                </div>
            ),
        },
        {
            title: "Тип посещение",
            dataIndex: "visitType",
            width: 130,
            className: "text-nowrap",
            render: (visitType) => (
                <Badge color="green">{getVisitTypeLabel(visitType)}</Badge>
            ),
        },
        {
            title: "Тип преглед",
            dataIndex: "examType",
            width: 120,
            className: "text-nowrap",
            render: (examType) => (
                <Badge color="orange">{getExamTypeLabel(examType)}</Badge>
            ),
        },
        {
            title: "Статус",
            dataIndex: "signed",
            width: 100,
            className: "text-nowrap",
            render: (signed) => (
                <Badge 
                    color={signed ? "green" : "orange"}
                    text={signed ? "Подписан" : "В процес"}
                />
            ),
        },
        {
            title: "Действия",
            width: 150,
            className: "text-nowrap",
            render: (text, record) => (
                <Space>
                    <Link 
                        to={`/patients/${patientId}/visits/${record.id}`}
                        className="btn btn-sm bg-info-light"
                    >
                        <FeatherIcon icon="eye" size={14} />
                        Преглед
                    </Link>
                    <Link 
                        to={`/patients/${patientId}/visits/${record.id}/edit`}
                        className="btn btn-sm bg-success-light"
                    >
                        <FeatherIcon icon="edit" size={14} />
                        Редактирай
                    </Link>
                </Space>
            ),
        },
    ];

    if (patientLoading || visitsLoading) {
        return <div>Зареждане...</div>;
    }

    return (
        <>
            <Header />
            <Sidebar id="menu-item2" id1="menu-items2" activeClassName="visits-history" />
            <div className="page-wrapper" style={{ overflowX: 'hidden' }}>
                <div className="content" style={{ overflowX: 'hidden', maxWidth: '100vw' }}>
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/patientslist-bulgarian">Пациенти</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to={`/patients/${patientId}/profile`}>
                                            {patient?.fullName?.given} {patient?.fullName?.family}
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    <li className="breadcrumb-item active">Медицински посещения</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Patient Info Card */}
                    <div className="row mb-3">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col">
                                            <h5 className="mb-1">
                                                {patient?.fullName?.given} {patient?.fullName?.sur} {patient?.fullName?.family}
                                            </h5>
                                            <p className="mb-0 text-muted">
                                                ЕГН: {patient?.egn} | РЗОК: {patient?.rzok} | 
                                                Здр. район: {patient?.zdrRajon} |
                                                <Badge color={patient?.isHealthInsurance ? "green" : "red"} className="ms-2">
                                                    {patient?.isHealthInsurance ? "Осигурен" : "Неосигурен"}
                                                </Badge>
                                            </p>
                                        </div>
                                        <div className="col-auto">
                                            <Link 
                                                to={`/patients/${patientId}/visits/new`}
                                                className="btn btn-primary"
                                            >
                                                <FeatherIcon icon="plus" size={16} className="me-1" />
                                                Ново посещение
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Visits Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card card-table show-entire">
                                <div className="card-body">
                                    {/* Table Header */}
                                    <div className="page-table-header mb-2">
                                        <div className="row align-items-center">
                                            <div className="col">
                                                <div className="doctor-table-blk" style={{ maxWidth: '100%', overflow: 'hidden' }}>
                                                    <h3>Медицински посещения ({visits.length})</h3>
                                                    <div className="doctor-search-blk" style={{ maxWidth: '100%' }}>
                                                        <div className="top-nav-search table-search-blk" style={{ maxWidth: '400px' }}>
                                                            <form>
                                                                <input 
                                                                    type="text" 
                                                                    className="form-control" 
                                                                    placeholder="Търсене по № амбулаторен лист, диагноза, МКБ код..."
                                                                    value={searchTerm}
                                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                                    style={{ maxWidth: '350px' }}
                                                                />
                                                                <Link className="btn">
                                                                    <img src={searchnormal} alt="Търсене" />
                                                                </Link>
                                                            </form>
                                                        </div>
                                                        <div className="add-group">
                                                            <Link 
                                                                to={`/patients/${patientId}/visits/new`} 
                                                                className="btn btn-primary add-pluss ms-2"
                                                            >
                                                                <img src={plusicon} alt="Добави" />
                                                            </Link>
                                                            <Button 
                                                                className="btn btn-primary doctor-refresh ms-2"
                                                                onClick={() => refetch()}
                                                            >
                                                                <img src={refreshicon} alt="Обнови" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div 
                                        className="table-responsive doctor-list" 
                                        style={{ 
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            WebkitOverflowScrolling: 'touch',
                                            scrollbarWidth: 'thin',
                                            msOverflowStyle: 'auto'
                                        }}
                                    >
                                        <Table
                                            pagination={{
                                                total: filteredVisits.length,
                                                showTotal: (total, range) =>
                                                    `Показване на ${range[0]} до ${range[1]} от ${total} записа`,
                                                showSizeChanger: true,
                                                onShowSizeChange: onShowSizeChange,
                                                itemRender: itemRender,
                                            }}
                                            columns={columns}
                                            dataSource={filteredVisits}
                                            rowSelection={rowSelection}
                                            rowKey={(record) => record.id}
                                            loading={visitsLoading}
                                            showSorterTooltip={false}
                                            style={{
                                                width: '100%',
                                                tableLayout: 'fixed'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Visit Modal */}
            <div
                id="delete_visit"
                className="modal fade delete-modal"
                role="dialog"
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body text-center">
                            <img src={imagesend} alt="" width={50} height={46} />
                            <h3>Сигурни ли сте, че искате да изтриете това посещение?</h3>
                            <div className="m-t-20">
                                <Link to="#" className="btn btn-white" data-bs-dismiss="modal">
                                    Затвори
                                </Link>
                                <button type="submit" className="btn btn-danger">
                                    Изтрий
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PatientVisitsHistory;